<script setup lang="ts" generic="T extends WorkStepDto">
import { WorkStepDto } from '@/api/generator';
import { useTaskStepTitle } from '@/components/TaskStepTitle/useTaskStepTitles';
import { provideAlignedTargetWidth } from '@/components/TaskStepTitle/useAlignedTargetWidth';
import { computed, ref, watch } from 'vue';
import { truthy } from '@/utils/helper';
import TaskStepTitle from '@/components/TaskStepTitle/TaskStepTitle.vue';
import EvaluationState from '@/components/evaluation/EvaluationState.vue';
import { useJobOccurrence } from '../utils/useJobOccurrence';
import { useFormMode } from '@/components/forms/useFormMode';
import ParameterTable from '@/components/ParameterTable/ParameterTable.vue';
import { mdiPlus } from '@mdi/js';
import CommentInlineDialog from '@/components/comments/CommentInlineDialog.vue';
import CommentCount from '@/components/forms/CommentCount/CommentCount.vue';

type InitialExpand = 'All' | 'First' | 'None';

const props = withDefaults(
  defineProps<{
    workSteps?: T[];
    initialExpand?: InitialExpand;
  }>(),
  {
    workSteps: undefined,
    initialExpand: 'None',
  },
);

const mode = useFormMode();

const { isWorkStepSkipable, toggleSkipWorkstep, reload, targetSystem } = useJobOccurrence();

provideAlignedTargetWidth();

const allCombinations = computed(() =>
  props.workSteps?.flatMap((step) => step.combination).filter(truthy),
);

const { getTitle } = useTaskStepTitle(allCombinations);

function getExpanded(initialExpand: InitialExpand) {
  switch (initialExpand) {
    case 'All':
      return props.workSteps?.map((_, index) => index) ?? [];
    case 'First':
      return props.workSteps ? [0] : [];
    case 'None':
      return [];
  }
}

const expanded = ref<number[]>(getExpanded(props.initialExpand));

const openCommentsDialogId = ref<string | undefined>(undefined);

function toggleCommentsDialog(open?: boolean, id?: string) {
  openCommentsDialogId.value = open ? id : undefined;
}

watch(
  () => props.initialExpand,
  (expandStrategy) => {
    expanded.value = getExpanded(expandStrategy);
  },
);
</script>

<template>
  <v-expansion-panels multiple :modelValue="expanded">
    <v-expansion-panel v-for="(step, index) in workSteps" :key="index">
      <template #title>
        <div class="d-flex w-100 align-center">
          <div class="me-auto align-center">
            <TaskStepTitle
              :combination="getTitle(step.combination)"
              :targetName="targetSystem?.name || undefined"
              mergeSets
            />
          </div>

          <div>
            <slot name="notification" :step="step" />
          </div>

          <div>
            <CommentCount :value="step" @click.stop="toggleCommentsDialog(true, step.id)" />
          </div>

          <div class="ml-3 mr-3">
            <EvaluationState :value="step.workState" />
          </div>

          <div class="d-flex align-center">
            <slot name="actions" :step="step" />
          </div>
        </div>
      </template>

      <CommentInlineDialog
        :entity="step"
        entityWithCommentType="WorkStep"
        :open="openCommentsDialogId === step.id"
        @update:open="toggleCommentsDialog($event, step.id)"
        @change="reload()"
      >
        <template #default> <div /> </template>
      </CommentInlineDialog>

      <template #text>
        <div class="d-flex justify-end align-center ga-4">
          <TextVBtn
            :prependIcon="mdiPlus"
            :text="$t('AddComment')"
            size="x-small"
            @click="toggleCommentsDialog(true, step.id)"
          />

          <v-checkbox
            v-if="isWorkStepSkipable(step) && mode === 'execution'"
            :label="$t('JobExecutionSkip')"
            density="compact"
            hideDetails
            :modelValue="step.workState === 'Skipped'"
            @click.stop="toggleSkipWorkstep(step)"
          />
        </div>

        <v-row class="mb-2">
          <v-col v-if="step.combination?.targetSystemValues?.length" cols="6">
            <ParameterTable
              v-if="step.combination?.targetSystemValues"
              :title="$t('TargetSystemParameter_other')"
              :parameters="step.combination?.targetSystemValues || undefined"
            />
          </v-col>

          <v-col cols="6">
            <ParameterTable
              v-if="step.combination?.supportSystemValues"
              :title="$t('SupportSystemParameter_other')"
              :parameters="step.combination?.supportSystemValues || undefined"
            />
          </v-col>
        </v-row>

        <slot name="workitem" :step="step" />
      </template>
    </v-expansion-panel>
  </v-expansion-panels>
</template>

<style scoped lang="scss">
:deep(.tasks) {
  .v-expansion-panel-title {
    padding: 0 16px 0 8px;
  }
}
</style>
